import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer";
import Stepper from "Components/presetQuotes/Stepper";
import ManageItemModal from "Components/Quotes/ManageQuotes/ManageItemModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  VALIDATION_LIMITS,
  createNameValidation,
  createOptionalDescriptionValidation,
} from "Components/constants/validation";
import AddCategoryModal from "Components/Quotes/ManageQuotes/AddCategoryModal";
import AddSubCategoryModal from "Components/Quotes/ManageQuotes/AddSubCategoryModal";
import ActionMenu from "Components/Common/ActionMenu";
import CustomInput from "Components/Custom/CustomInput";
import CustomButton from "Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import {
  createPresetQuoteTemplate,
  deleteItem,
  getItems,
  getPresetQuoteDetail,
  updatePresetQuoteTemplate,
} from "slices/thunks";
import { useLocation, useNavigate } from "react-router-dom";
import { clearPresetQuoteDetail } from "slices/presetQuotes/reducer";
import { ItemData } from "types/item.api";
import { ErrorHandler, SuccessToast } from "utils/toast";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import { addItemsToState, findAndReplaceAddedItem } from "slices/items/reducer";
import Loader from "Components/Common/Loader";

interface addPresetQuoteParams {
  templateName: string;
  jobDescription: string;
}

const ManagePresetQuotes = () => {
  const { state } = useLocation();
  const [editablePresetQuote] = useState<Boolean>(
    state?.templateId ? true : false,
  );
  const [customActiveTab, setCustomActiveTab] = useState<string>("1");
  const [showManageItemModal, setShowManageItemModal] = useState(false);
  const [isEditItemModal, setIsEditItemModal] = useState<boolean>(false);
  const [showAddCategoryModal, setShowAddCategoryModal] =
    useState<boolean>(false);
  const [showAddSubCategoryModal, setShowAddSubCategoryModal] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { presetQuoteDetail, isLoading } = useAppSelector(
    (state) => state?.presetQuote,
  );
  const {
    items,
    pagination: meta,
    status,
    addItems,
  } = useAppSelector((state) => state?.item);
  const [templateId, setTemplateId] = useState<number>(state?.templateId);
  const [isEdit, setIsEdit] = useState(!!state?.templateId);
  const [quantity, setQuantity] = useState<number>(0);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemData | null>(null);
  const [isSummaryCompleted, setIsSummaryCompleted] = useState(
    !!state?.templateId,
  );

  //Preset Quote Detail Api Call
  useEffect(() => {
    const apiCall = async () => {
      try {
        setIsInitialLoading(true);
        const res = await dispatch(
          getPresetQuoteDetail({ templateId: templateId }),
        )?.unwrap();
        dispatch(addItemsToState(res?.payload?.items || []));
      } catch (error) {
        ErrorHandler(error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    if (isEdit && templateId) {
      apiCall();
      return () => {
        dispatch(clearPresetQuoteDetail());
      };
    }
  }, [dispatch, isEdit, templateId]);

  useEffect(() => {
    if (customActiveTab === "2") {
      const params = searchText
        ? { search: searchText, quote_template_id: templateId }
        : { page: currentPage, quote_template_id: templateId };
      dispatch(getItems(params));
    }
  }, [customActiveTab, dispatch, currentPage, searchText, templateId]);

  const toggleManageItemModal = useCallback(() => {
    setSelectedItem(null);
    setShowManageItemModal((prev) => !prev);
  }, []);

  const handleDeleteItem = useCallback(() => {
    dispatch(deleteItem(selectedItem?.id!));
    setShowDeleteModal(false);
    setSelectedItem(null);
  }, [dispatch, selectedItem?.id]);

  const toggleAddCategoryModal = useCallback(() => {
    setShowAddCategoryModal((prev) => !prev);
  }, []);

  const toggleAddSubCategoryModal = useCallback(() => {
    setShowAddSubCategoryModal((prev) => !prev);
  }, []);

  const toggleCustom = (tab: any) => {
    if (customActiveTab !== tab) {
      setCustomActiveTab(tab);
    }
  };

  // =========================
  // Formik Validation Schema
  // =========================
  const templateNameValidation = createNameValidation("Template Name");
  const jobDescriptionValidation =
    createOptionalDescriptionValidation("Job Description");

  const validationSchema = Yup.object({
    templateName: Yup.string()
      .required("Please Enter Template Name")
      .trim()
      .min(
        VALIDATION_LIMITS.NAME.MIN,
        `Template Name must be at least ${VALIDATION_LIMITS.NAME.MIN} characters`,
      )
      .max(
        VALIDATION_LIMITS.NAME.MAX,
        `Template Name must be at most ${VALIDATION_LIMITS.NAME.MAX} characters`,
      )
      .test(
        "valid-template-name",
        templateNameValidation.message,
        templateNameValidation.test,
      ),
    jobDescription: Yup.string()
      .trim()
      .max(
        VALIDATION_LIMITS.DESCRIPTION.MAX,
        `Job Description must be at most ${VALIDATION_LIMITS.DESCRIPTION.MAX} characters`,
      )
      .test(
        "valid-job-description",
        jobDescriptionValidation.message,
        jobDescriptionValidation.test,
      ),
  });

  // =========================
  // Formik Setup
  // =========================
  const formik = useFormik<addPresetQuoteParams>({
    enableReinitialize: true,
    initialValues: {
      templateName:
        isEdit && presetQuoteDetail?.name?.trim()
          ? presetQuoteDetail?.name?.trim()
          : "",
      jobDescription:
        isEdit && presetQuoteDetail?.description?.trim()
          ? presetQuoteDetail?.description?.trim()
          : "",
    },
    validationSchema,
    onSubmit: async () => {
      try {
        const values = formik.values;
        const payloadData = {
          name: values.templateName?.trim(),
          description: values.jobDescription?.trim(),
        };

        console.log("payloadData", payloadData, isEdit, presetQuoteDetail);

        if (
          isEdit &&
          presetQuoteDetail?.name === payloadData?.name &&
          presetQuoteDetail?.description === (payloadData?.description || null)
        ) {
          setCustomActiveTab("2");
          return;
        }

        if (isEdit && templateId) {
          const res = await dispatch(
            updatePresetQuoteTemplate({
              data: { ...payloadData, id: templateId },
            }),
          )?.unwrap();
          SuccessToast(res?.message);
        } else {
          const res = await dispatch(
            createPresetQuoteTemplate({ data: payloadData }),
          )?.unwrap();
          setTemplateId(res?.payload?.id);
          setIsEdit(true);
          SuccessToast(res?.message);
          if (res.result) {
            setIsSummaryCompleted(true);
          }
        }
        setCustomActiveTab("2");
      } catch (error) {
        ErrorHandler(error);
      }
    },
  });

  // Memoize quantity change handler
  const handleQuantityChange = useCallback(
    (item: ItemData, quantity: number) => {
      dispatch(findAndReplaceAddedItem({ ...item, quantity }));
      setQuantity(quantity);
    },
    [dispatch],
  );

  const columns = useMemo(
    () => [
      {
        header: "ITEM NAME",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "CATEGORY",
        accessorKey: "category_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "SUBCATEGORY",
        accessorKey: "subcategory_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "UNIT",
        accessorKey: "unit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "PRICE/UNIT",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "QUANTITY",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          // console.log("addedItems", addedItems);
          const foundItem = addItems.find((item) => item.id === rowData.id);
          let currentQuantity = 0;
          if (foundItem) {
            currentQuantity = foundItem.quantity;
          }

          return (
            <Stepper
              isEdit={isEdit}
              initialQuantity={currentQuantity}
              key={`${rowData.id}-${currentQuantity}`}
              onQuantityChange={(quantity) =>
                handleQuantityChange(rowData, quantity)
              }
            />
          );
        },
      },
      {
        header: "TOTAL",
        accessorKey: "total_price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          const foundItem = addItems.find((item) => item.id === rowData.id);
          let currentQuantity = 0;
          if (foundItem) {
            currentQuantity = foundItem.quantity;
          }

          const total = Number(currentQuantity) * Number(rowData.price);
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              £{total.toFixed(2)}
            </span>
          );
        },
      },
      {
        header: "ACTIONS",
        enableSorting: false,

        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <ActionMenu
              onEdit={() => {
                setIsEditItemModal(true);
                setShowManageItemModal(true);
                setSelectedItem({ ...rowData, quantity });
              }}
              onDelete={() => {
                setShowDeleteModal(true);
                setSelectedItem(rowData);
              }}
            />
          );
        },
      },
    ],
    [addItems, handleQuantityChange, isEdit, quantity],
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleAddPresetQuoteTemplate = useCallback(async () => {
    if (!templateId) {
      return;
    }
    try {
      const payloadData = {
        id: templateId,
        items: addItems,
      };

      console.log("payloadData for preset quote template", payloadData);

      const res = await dispatch(
        updatePresetQuoteTemplate({ data: payloadData }),
      )?.unwrap();
      SuccessToast(res?.message);
      navigate("/preset-quotes");
      formik.resetForm();
    } catch (error) {
      ErrorHandler(error);
    }
  }, [addItems, dispatch, formik, navigate, templateId]);

  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  document.title = editablePresetQuote
    ? "Edit Quote Preset | EaziQuote"
    : "Add Quote Preset | EaziQuote";

  if (isInitialLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Loader />
      </Container>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={
              editablePresetQuote ? "Edit Quote Preset" : "Add Quote Preset"
            }
            pageTitle={"Preset Quotes"}
          />
          <Col lg={12}>
            <div className="d-flex align-items-center justify-content-between w-100 mb-4">
              {/* LEFT TITLE AREA */}
              <div className="text-black2D fw-bold fs-24 font-primary p-0 m-0 text-nowrap">
                <p className="fs-3 fw-bold mb-1">
                  {editablePresetQuote
                    ? "Edit Quote Preset"
                    : "Add Quote Preset"}
                </p>
              </div>

              {/* RIGHT BUTTON */}
            </div>
          </Col>
          <Card className="br-10 bg-white">
            <CardBody>
              <Row className="row align-items-end  mb-4 border-bottom">
                <Col>
                  <div id="mail-filter-navlist">
                    <Nav
                      className="nav nav-tabs nav-tabs-custom nav-primary gap-1 text-center border-bottom-0"
                      role="tablist"
                    >
                      <NavItem>
                        <NavLink
                          className={classnames({
                            active: customActiveTab === "1",
                          })}
                          onClick={() => {
                            toggleCustom("1");
                          }}
                          style={{
                            fontSize: 16,
                            fontWeight: customActiveTab === "1" ? 500 : 400,
                            fontFamily: "Inter Tight",
                            color:
                              customActiveTab === "1" ? "#082B60" : "#2D2D2D",
                          }}
                        >
                          Summary
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({
                            active: customActiveTab === "2",
                          })}
                          onClick={() => {
                            toggleCustom("2");
                          }}
                          style={{
                            fontSize: 16,
                            fontWeight: customActiveTab === "3" ? 500 : 400,
                            fontFamily: "Inter Tight",
                            color:
                              !isSummaryCompleted && !isEdit
                                ? "#B0B0B0"
                                : customActiveTab === "3"
                                ? "#082B60"
                                : "#2D2D2D",
                            cursor:
                              isSummaryCompleted || isEdit
                                ? "pointer"
                                : "not-allowed",
                            opacity: isSummaryCompleted || isEdit ? 1 : 0.6,
                          }}
                          disabled={
                            !isEdit && !isSummaryCompleted && !templateId
                          }
                        >
                          Items
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </Col>
              </Row>

              <TabContent activeTab={customActiveTab}>
                <TabPane tabId="1" id="home1">
                  <form
                    onSubmit={(e) => {
                      e?.preventDefault();
                      formik?.handleSubmit();
                    }}
                  >
                    <Row className="g-1">
                      <Col lg={12}>
                        <CustomInput
                          label="Template Name"
                          type="text"
                          name="templateName"
                          value={formik?.values?.templateName}
                          onChange={formik?.handleChange}
                          onBlur={formik?.handleBlur}
                          error={formik?.errors?.templateName}
                          touched={formik?.touched?.templateName}
                          placeholder="e.g. Standard Boiler Install"
                        />
                      </Col>
                      <Col lg={12}>
                        <CustomInput
                          label="Job Description"
                          type="textarea"
                          name="jobDescription"
                          value={formik?.values?.jobDescription}
                          onChange={formik?.handleChange}
                          onBlur={formik?.handleBlur}
                          error={formik?.errors?.jobDescription}
                          touched={formik?.touched?.jobDescription}
                          placeholder="Enter job description..."
                        />
                      </Col>

                      <Col lg={12}>
                        <CustomButton
                          text="Save"
                          className="bg-primary br-7 px-5"
                          type="submit"
                          loading={isLoading}
                        />
                      </Col>
                    </Row>
                  </form>
                </TabPane>
                <TabPane tabId="2">
                  <TableContainer
                    columns={columns}
                    data={items}
                    isGlobalFilter={true}
                    SearchPlaceholder="Search items"
                    divClass="table-responsive table-card mb-2 bg-white"
                    tableClass="align-middle table-nowrap table-borderless mb-0 "
                    thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
                    theadClass="table-light"
                    isPresetsFilter={true}
                    searchAlign="left"
                    onAddItemClick={() => {
                      setShowManageItemModal(true);
                      setIsEditItemModal(false);
                    }}
                    customPageSize={meta?.per_page}
                    totalPages={meta?.last_page}
                    currentPage={meta?.current_page}
                    fromPage={meta?.from}
                    toPage={meta?.to}
                    totalItems={meta?.total}
                    onPageChange={handlePageChange}
                    isInitialLoading={
                      status?.fetch === "loading" && !items?.length
                    }
                    isPaginationLoading={
                      status?.fetch === "loading" && items?.length > 0
                    }
                    onSearch={handleSearch}
                  />
                  <Col lg={12}>
                    <div
                      className="dashed my-4"
                      style={{ marginRight: -15, marginLeft: -15 }}
                    />
                    <CustomButton
                      text="Save Preset"
                      className="bg-primary br-7 px-5"
                      loading={isLoading}
                      onClick={handleAddPresetQuoteTemplate}
                    />
                  </Col>
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
          <ManageItemModal
            showModal={showManageItemModal}
            toggle={toggleManageItemModal}
            onAddCategoryClick={toggleAddCategoryModal}
            onAddSubCategoryClick={toggleAddSubCategoryModal}
            selectedItem={selectedItem}
            quoteDetails={null}
            invoiceDetails={null}
            presetQuoteDetails={presetQuoteDetail}
            isEdit={isEditItemModal}
          />
          <AddCategoryModal
            show={showAddCategoryModal}
            onCloseClick={toggleAddCategoryModal}
          />
          <AddSubCategoryModal
            show={showAddSubCategoryModal}
            onCloseClick={toggleAddSubCategoryModal}
          />
          <DeleteItemModal
            show={showDeleteModal}
            onCloseClick={() => {
              setShowDeleteModal(false);
            }}
            onDeleteClick={handleDeleteItem}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ManagePresetQuotes;
